import { FunctionComponent, useCallback, useEffect } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/router'

import useAppTheme from '@hooks/useAppTheme'
import useAuthentication from '@hooks/useAuthentication'
import useIframeRefs, { IframeRefType } from '@hooks/useIframeRefs'
import useTracking, { TrackingFnType } from '@hooks/useTracking'

export type GenericMessage = {
  iframeId: string
}

export interface NavigateToMessage extends GenericMessage {
  type: 'navigateTo'
  blick_event_type: 'navigateTo'
  href: string
}

export interface InWebviewNavigationMessage extends GenericMessage {
  type: 'IN_WEBVIEW_NAVIGATION'
  blick_event_type: 'IN_WEBVIEW_NAVIGATION'
}

export interface CMPEnableAllCategoriesMessage extends GenericMessage {
  type: 'CMP_ENABLE_ALL_CATEGORIES'
  blick_event_type: 'CMP_ENABLE_ALL_CATEGORIES'
}

export interface CMPShowMoreInfoMessage extends GenericMessage {
  type: 'CMP_SHOW_MORE_INFO'
  blick_event_type: 'CMP_SHOW_MORE_INFO'
}

export interface ResizeMessage extends GenericMessage {
  type: 'resize'
  blick_event_type: 'resize'
  height: number
  width: number
}

export interface IFrameReadyMessage extends GenericMessage {
  type: 'iframeReady'
  blick_event_type: 'iframeReady'
  iframeReady: true
}

export interface LoginActionMessage extends GenericMessage {
  type: undefined
  blick_event_type: undefined
  loginAction: true
  ssoTrackingSource?: string
}

export interface TrackActionMessage extends GenericMessage {
  type: 'TRACK'
  blick_event_type: 'TRACK'
  name: string
  data: Record<string, unknown>
}

export interface LoginMessage extends GenericMessage {
  type: 'LOGIN'
  blick_event_type: 'LOGIN'
  loginCase?: string
  source?: string
}

export interface NavigateMessage extends GenericMessage {
  type: 'NAVIGATE'
  blick_event_type: 'NAVIGATE'
  href: string
}

export type PostMessage =
  | NavigateToMessage
  | InWebviewNavigationMessage
  | CMPEnableAllCategoriesMessage
  | CMPShowMoreInfoMessage
  | ResizeMessage
  | IFrameReadyMessage
  | LoginActionMessage
  | TrackActionMessage
  | LoginMessage
  | NavigateMessage

const isSafeUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url)
    return parsedUrl.origin === window.location.origin
  } catch {
    return !!url && url?.[0] === '/' && url?.[1] !== '/'
  }
}

const PostMessageManager: FunctionComponent = () => {
  const router = useRouter()
  const { login } = useAuthentication()
  const queryClient = useQueryClient()
  const { getIframesByIdOrUrl } = useIframeRefs()
  const appTheme = useAppTheme()

  const onIframeTrackEventReceived = useCallback<
    TrackingFnType<{ eventName: string; eventData: Record<string, unknown> }>
  >(
    ({ extraData }) => ({
      event: extraData.eventName,
      ...extraData.eventData,
    }),
    []
  )

  const onTrackEventReceived = useTracking(onIframeTrackEventReceived)

  const onHandlePostMessage = useCallback(
    (event: MessageEvent<PostMessage>) => {
      const { data } = event
      // The value of 'iframeId' set in the event can be
      // a unique nanoid in case of embedded code,
      // or the iframe url for all the other cases
      const iframeIdOrUrl = data?.iframeId

      if (!iframeIdOrUrl) {
        return
      }

      const iframeRefs = queryClient.getQueryData<IframeRefType[]>([
        'iframe-refs',
      ])

      // Safety check to be sure the event sent via post message comes from one of the expected iframes
      if (!iframeRefs) {
        return
      }

      if (
        ![
          ...iframeRefs.map((iframe) => iframe.id),
          ...iframeRefs.map((iframe) => iframe.url).filter((url) => !!url),
        ].includes(iframeIdOrUrl)
      ) {
        return
      }

      const { type, blick_event_type } = data

      if (blick_event_type) {
        if (blick_event_type === 'iframeReady') {
          const iframeRefs = getIframesByIdOrUrl(iframeIdOrUrl)
          iframeRefs?.forEach((iframeRef) => {
            if (iframeRef.callback) {
              iframeRef.callback()
            }
          })
          return
        }

        if (blick_event_type === 'TRACK') {
          const { name: eventName, data: eventData } = data
          onTrackEventReceived({ eventName, eventData })
          return
        }

        if (blick_event_type === 'resize') {
          const { height, iframeId } = data

          const iframeRefs = getIframesByIdOrUrl(iframeId)
          iframeRefs?.forEach((iframeRef) => {
            const currentIFrameHeight =
              iframeRef?.containerRef?.current?.clientHeight
            if (
              !!iframeRef?.containerRef?.current &&
              !isNaN(height) &&
              height !== currentIFrameHeight
            ) {
              iframeRef.containerRef.current.style.height = `${height}px`
            }
          })
          return
        }

        if (blick_event_type === 'navigateTo') {
          if (isSafeUrl(data.href)) {
            router.push(data.href)
          }
          return
        }

        if (blick_event_type === 'IN_WEBVIEW_NAVIGATION') {
          window.scrollTo(0, 0)
          return
        }

        if (blick_event_type === 'CMP_ENABLE_ALL_CATEGORIES') {
          window.OneTrust?.AllowAll()
          return
        }

        if (blick_event_type === 'CMP_SHOW_MORE_INFO') {
          window.OneTrust?.ToggleInfoDisplay()
          return
        }

        if (blick_event_type === 'NAVIGATE') {
          if (isSafeUrl(data.href)) {
            router.push(data.href)
          }
          return
        }

        if (blick_event_type === 'LOGIN') {
          login({
            loginCase: data.loginCase ?? 'email_only',
            prefix: data.source ?? 'IFR',
          })
          return
        }
      } else if (type) {
        //! This has to map blick_event_type, due to migration reasons.
        //! It should be removed at the end.
        if (type === 'iframeReady') {
          // Check on the list which iframes have url === hostedIFrameId
          const iframeRefs = getIframesByIdOrUrl(iframeIdOrUrl)
          iframeRefs?.forEach((iframeRef) => {
            if (iframeRef.callback) {
              iframeRef.callback()
            }
          })
          return
        }

        if (type === 'TRACK') {
          const { name: eventName, data: eventData } = data
          onTrackEventReceived({ eventName, eventData })
          return
        }

        if (type === 'resize') {
          const { height, iframeId } = data

          const iframeRefs = getIframesByIdOrUrl(iframeId)
          iframeRefs?.forEach((iframeRef) => {
            const currentIFrameHeight =
              iframeRef?.containerRef?.current?.clientHeight
            if (
              !!iframeRef?.containerRef?.current &&
              !isNaN(height) &&
              height !== currentIFrameHeight
            ) {
              iframeRef.containerRef.current.style.height = `${height}px`
            }
          })
          return
        }

        if (type === 'navigateTo') {
          if (isSafeUrl((data as NavigateToMessage).href)) {
            router.push((data as NavigateToMessage).href)
          }
          return
        }

        if (type === 'IN_WEBVIEW_NAVIGATION') {
          window.scrollTo(0, 0)
          return
        }

        if (type === 'CMP_ENABLE_ALL_CATEGORIES') {
          window.OneTrust?.AllowAll()
          return
        }

        if (type === 'CMP_SHOW_MORE_INFO') {
          window.OneTrust?.ToggleInfoDisplay()
          return
        }

        if (type === 'NAVIGATE') {
          if (isSafeUrl((data as NavigateToMessage).href)) {
            router.push((data as NavigateMessage).href)
          }
          return
        }

        if (type === 'LOGIN') {
          login({
            loginCase: (data as LoginMessage).loginCase ?? 'email_only',
            prefix: (data as LoginMessage).source ?? 'IFR',
          })
          return
        }
      } else {
        // Certain embeds can trigger a login action through their post message
        // Here we check if the action was true and if it was sent by the actual component
        const { loginAction, ssoTrackingSource } = data
        if (loginAction) {
          login({
            loginCase: 'email_only',
            prefix: ssoTrackingSource ?? 'IFR',
          })
        }
        return
      }
    },
    [onTrackEventReceived, router, login, getIframesByIdOrUrl, queryClient]
  )

  useEffect(() => {
    window.addEventListener('message', onHandlePostMessage)

    return () => {
      window.removeEventListener('message', onHandlePostMessage)
    }
  }, [onHandlePostMessage])

  useEffect(() => {
    if (appTheme !== 'server') {
      const iframeRefs = queryClient.getQueryData<IframeRefType[]>([
        'iframe-refs',
      ])

      if (Array.isArray(iframeRefs)) {
        iframeRefs.forEach((iframeRef) => {
          if (iframeRef) {
            iframeRef.ref?.current?.contentWindow?.postMessage(
              { blick_event_type: 'CHANGE_THEME', theme: appTheme },
              '*'
            )
          }
        })
      }
    }
  }, [appTheme, queryClient])

  return null
}
export default PostMessageManager
